import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, rupiah } from "@/lib/api";
import { useCatalog } from "@/lib/useCatalog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhotoUploader } from "@/components/PhotoUploader";
import { PageLoader } from "@/components/layout/RequireAuth";
import { EmptyState } from "@/pages/CategoryPage";

const EMPTY = { title: "", subcategory: "", price: "", description: "", includes: "", duration: "", photo_path: null, active: true };

const PackageForm = ({ initial, subs, onSaved, onClose }) => {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: k === "price" ? e.target.value.replace(/\D/g, "") : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...f, price: Number(f.price), includes: f.includes.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (f.package_id) await api.put(`/provider/packages/${f.package_id}`, payload);
      else await api.post("/provider/packages", payload);
      toast.success("Paket tersimpan");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4" data-testid="package-form">
      <div className="space-y-2"><Label>Judul paket</Label><Input required value={f.title} onChange={set("title")} className="rounded-xl" data-testid="package-title-input" /></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Subkategori</Label>
          <Select value={f.subcategory} onValueChange={(v) => setF({ ...f, subcategory: v })}>
            <SelectTrigger className="rounded-xl" data-testid="package-subcategory-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
            <SelectContent>{subs.map((s) => <SelectItem key={s.slug} value={s.slug} data-testid={`package-subcategory-option-${s.slug}`}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Harga (Rp)</Label>
          <Input required inputMode="numeric" value={f.price ? Number(f.price).toLocaleString("id-ID") : ""} onChange={set("price")} className="rounded-xl" data-testid="package-price-input" />
        </div>
      </div>
      <div className="space-y-2"><Label>Estimasi durasi</Label><Input required value={f.duration} onChange={set("duration")} placeholder="mis. 2 jam" className="rounded-xl" data-testid="package-duration-input" /></div>
      <div className="space-y-2"><Label>Deskripsi</Label><Textarea required rows={3} value={f.description} onChange={set("description")} className="rounded-xl" data-testid="package-description-input" /></div>
      <div className="space-y-2"><Label>Yang termasuk (satu per baris)</Label><Textarea rows={4} value={f.includes} onChange={set("includes")} className="rounded-xl" data-testid="package-includes-input" /></div>
      <div className="space-y-2"><Label>Foto (opsional)</Label>
        <PhotoUploader value={f.photo_path ? [f.photo_path] : []} onChange={(v) => setF({ ...f, photo_path: v[0] || null })} max={1} testId="package-photo" />
      </div>
      <label className="flex items-center gap-3 text-sm text-stone-700">
        <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="package-active-switch" /> Paket aktif (tampil publik)
      </label>
      <Button type="submit" disabled={saving || !f.subcategory} className="w-full rounded-xl bg-teal-700 hover:bg-teal-800" data-testid="package-save-button">
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan Paket
      </Button>
    </form>
  );
};

export default function ProviderPackages() {
  const { user } = useAuth();
  const { subcategories } = useCatalog();
  const [items, setItems] = useState(null);
  const [mySubs, setMySubs] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/provider/packages").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/provider/profile").then((r) => setMySubs(r.data.subcategories));
  }, [user.user_id]);
  const subs = subcategories.filter((s) => mySubs.includes(s.slug));
  const subName = (slug) => subcategories.find((s) => s.slug === slug)?.name || slug;
  const toggle = async (p) => {
    try {
      await api.put(`/provider/packages/${p.package_id}`, { ...p, active: !p.active });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="container-page max-w-5xl py-10 lg:py-14" data-testid="provider-packages-page">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="eyebrow">Penyedia</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl">Paket Harga Tetap</h1>
          <p className="mt-2 text-stone-500">Pelanggan bisa langsung memesan paket tanpa menunggu penawaran.</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY, subcategory: subs[0]?.slug || "" })} disabled={!subs.length} className="rounded-full bg-teal-700 hover:bg-teal-800" data-testid="add-package-button">
          <Plus className="mr-1.5 h-4 w-4" /> Tambah paket
        </Button>
      </div>
      {!subs.length && items && <p className="mt-4 text-sm text-amber-800">Lengkapi kategori layanan di Profil Usaha terlebih dahulu.</p>}
      <div className="mt-8 space-y-3">
        {items === null ? <PageLoader /> : items.length === 0 ? <EmptyState testId="packages-empty" title="Belum ada paket" desc="Buat paket pertama Anda, misalnya 'Cuci AC 1 Unit'." /> :
          items.map((p) => (
            <div key={p.package_id} className={`card-soft flex flex-col gap-4 p-5 sm:flex-row sm:items-center ${p.active ? "" : "opacity-60"}`} data-testid={`my-package-${p.package_id}`}>
              <div className="flex-1">
                <p className="text-xs font-medium text-teal-700">{subName(p.subcategory)}</p>
                <p className="font-bold text-stone-900">{p.title}</p>
                <p className="text-sm text-stone-500">{p.duration} · {p.active ? "Aktif" : "Nonaktif"}</p>
              </div>
              <p className="text-xl font-extrabold text-stone-900">{rupiah(p.price)}</p>
              <div className="flex items-center gap-3">
                <Switch checked={p.active} onCheckedChange={() => toggle(p)} data-testid={`toggle-package-${p.package_id}`} />
                <Button variant="outline" size="sm" className="rounded-xl" data-testid={`edit-package-${p.package_id}`}
                  onClick={() => setEditing({ ...p, price: String(p.price), includes: p.includes.join("\n") })}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" /> Ubah
                </Button>
              </div>
            </div>
          ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-2xl">
          <DialogHeader><DialogTitle>{editing?.package_id ? "Ubah paket" : "Paket baru"}</DialogTitle></DialogHeader>
          {editing && <PackageForm initial={editing} subs={subs} onSaved={load} onClose={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
